import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PhoneInput from '../components/PhoneInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import { DataPagination } from '../components/DataPagination';
import {
  Users,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Eye,
  Trash,
  ShieldCheck,
  User as UserIcon,
  Buildings,
  DesktopTower,
  LockKey,
  LockKeyOpen,
  GraduationCap,
  Gauge,
  Prohibit,
  CheckCircle,
  DownloadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// API_URL imported from utils

const getRoleBadge = (role) => {
  switch (role) {
    case 'super_admin':
      return { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Super Admin', icon: ShieldCheck };
    case 'entity_admin':
      return { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Entity Admin', icon: Buildings };
    case 'campus_agent':
      return { bg: 'bg-[#10B981]/10', text: 'text-[#10B981]', label: 'Cashier', icon: UserIcon };
    case 'campus_correspondent':
      return { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Campus Correspondent', icon: GraduationCap };
    case 'instrumentation_ops':
      return { bg: 'bg-cyan-50', text: 'text-cyan-700', label: 'Instrumentation Operations', icon: Gauge };
    case 'back_office':
      return { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Back Office', icon: DesktopTower };
    case 'cx_agent':
      return { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', label: 'CX Agent', icon: UserIcon };
    case 'ambassador':
      return { bg: 'bg-teal-50', text: 'text-teal-700', label: 'Ambassador', icon: UserIcon };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-700', label: role, icon: UserIcon };
  }
};

export default function UsersPage() {
  const { isSuperAdmin, isEntityAdmin, user: currentUser } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    email: '',
    fullname: '',
    role: '',
    entity_id: '',
    campus_id: '',
    mobile: ''
  });
  const [campusOptions, setCampusOptions] = useState([]);
  const [loadingCampuses, setLoadingCampuses] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/users`;
      if (filterEntity && filterEntity !== 'all') {
        url += `?entity_id=${filterEntity}`;
      }
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      password: '',
      email: '',
      fullname: '',
      role: isSuperAdmin ? 'entity_admin' : 'campus_agent',
      entity_id: isSuperAdmin ? '' : (selectedEntity?.id || ''),
      campus_id: '',
      mobile: ''
    });
    setEditingUser(null);
  };

  // Pull campuses for the currently-selected entity whenever the form needs
  // them (Campus Correspondent role).
  useEffect(() => {
    const needsCampus = formData.role === 'campus_correspondent' && formData.entity_id;
    if (!needsCampus) { setCampusOptions([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingCampuses(true);
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API_URL}/api/campuses?entity_id=${formData.entity_id}`, { headers });
        if (!cancelled) setCampusOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setCampusOptions([]);
      } finally {
        if (!cancelled) setLoadingCampuses(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.role, formData.entity_id]);

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const [exporting, setExporting] = useState(false);
  const exportUsers = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/users/export.xlsx`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-users_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('System users exported');
    } catch (e) {
      toast.error(formatApiError(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      password: '',
      email: user.email,
      fullname: user.fullname,
      role: user.role,
      entity_id: user.entity_id || '',
      campus_id: user.campus_id || '',
      mobile: user.mobile || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard: campus_correspondent must have a campus before we even hit the server.
    if (formData.role === 'campus_correspondent' && !formData.campus_id) {
      toast.error('Please select a campus for the Campus Correspondent.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.username;
        // Drop campus_id payload for non-correspondent roles so the backend
        // clears the pin (the API treats missing keys as "no change", so we
        // must NOT include it as empty string here when irrelevant).
        if (updateData.role !== 'campus_correspondent') {
          delete updateData.campus_id;
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData, {
          withCredentials: true, headers
        });
        toast.success('User updated successfully');
      } else {
        const createData = { ...formData };
        if (createData.role !== 'campus_correspondent') {
          delete createData.campus_id;
        }
        await axios.post(`${API_URL}/api/users`, createData, {
          withCredentials: true, headers
        });
        toast.success('User created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const message = formatApiError(error, 'Failed to save user');
      toast.error(message);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error("You cannot delete your own account");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${user.fullname}?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL}/api/users/${user.id}`, {
        withCredentials: true, headers
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleUnlock = async (user) => {
    if (!window.confirm(`Unlock ${user.fullname}? They will be able to log in immediately.`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/api/users/${user.id}/unlock`, {}, { withCredentials: true, headers });
      toast.success(`${user.fullname} unlocked`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to unlock user');
    }
  };

  const handleToggleStatus = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    const next = user.status === 'active' ? 'inactive' : 'active';
    const msg = next === 'inactive'
      ? `Deactivate ${user.fullname}? They will no longer be able to log in.`
      : `Activate ${user.fullname}? They will be able to log in again.`;
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_URL}/api/users/${user.id}`, { status: next }, { withCredentials: true, headers });
      toast.success(`${user.fullname} ${next === 'inactive' ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update user status'));
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Pagination (client-side over filtered list)
  const totalFiltered = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  // Reset to page 1 whenever filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterEntity, filterRole]);

  if (!isSuperAdmin && !isEntityAdmin) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-8 text-center">
        <p className="text-[#78716C]">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  // Available roles based on current user
  const availableRoles = isSuperAdmin
    ? [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'entity_admin', label: 'Entity Admin' },
      ]
    : [
        { value: 'campus_agent', label: 'Cashier' },
        { value: 'campus_correspondent', label: 'Campus Correspondent' },
        { value: 'instrumentation_ops', label: 'Instrumentation Operations' },
        { value: 'back_office', label: 'Back Office' },
        { value: 'cx_agent', label: 'CX Agent' },
      ];

  return (
    <div className="space-y-3 pb-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Users
        </h1>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button
              onClick={exportUsers}
              disabled={exporting}
              variant="outline"
              className="border-[#EDE6D8] text-[#0A2540] rounded-lg"
              data-testid="export-users-btn"
            >
              <DownloadSimple size={18} className="mr-2" />
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          )}
          <Button
            onClick={openCreateDialog}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="create-user-btn"
          >
            <Plus size={18} className="mr-2" />
            {isSuperAdmin ? 'Add Entity Admin' : 'Add User'}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name, username, email…"
        testId="users-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); setFilterRole('all'); }}
        filters={[
          ...(isSuperAdmin ? [{
            key: 'entity',
            label: 'Entity',
            value: filterEntity,
            onChange: setFilterEntity,
            options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
          }] : []),
          {
            key: 'role',
            label: 'Role',
            value: filterRole,
            onChange: setFilterRole,
            options: [
              { value: 'all', label: 'All roles' },
              { value: 'entity_admin', label: 'Entity Admin' },
              { value: 'campus_agent', label: 'Cashier' },
              { value: 'campus_correspondent', label: 'Campus Correspondent' },
              { value: 'instrumentation_ops', label: 'Instrumentation Operations' },
              { value: 'back_office', label: 'Back Office' },
              { value: 'cx_agent', label: 'CX Agent' },
              { value: 'ambassador', label: 'Ambassador' },
            ],
          },
        ]}
      />

      <DataTable
        columns={[
          { key: 'user', label: 'User', width: 'minmax(220px,2fr)', render: (u) => (
            <span className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#0A2540]/10 group-hover:bg-[#EDE6D8] flex items-center justify-center">
                <span className="text-[10px] font-bold text-[#0A2540] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {(u.fullname || 'U').charAt(0).toUpperCase()}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {u.fullname}
                </span>
              </span>
            </span>
          ) },
          { key: 'role', label: 'Role', width: 'minmax(140px,1.2fr)', render: (u) => {
            const badge = getRoleBadge(u.role);
            const Icon = badge.icon;
            return (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${badge.bg} ${badge.text} group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]`}>
                <Icon size={11} />
                {badge.label}
              </span>
            );
          } },
          { key: 'contact', label: 'Contact', width: 'minmax(180px,1.5fr)', tone: 'muted', mono: true, render: (u) => u.email || u.mobile || '—' },
          { key: 'status', label: 'Status', width: 'minmax(110px,1fr)', render: (u) => (
            <span className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
              <span className={`text-xs font-medium ${u.status === 'active' ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {u.status || '—'}
              </span>
              {u.locked && <LockKey size={10} weight="fill" className="text-amber-600 group-hover:text-[#1C1917]" data-testid={`locked-badge-${u.id}`} />}
            </span>
          ) },
          { key: 'last_login', label: 'Last Login', width: 'minmax(110px,1fr)', mono: true, tone: 'muted',
            render: (u) => u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never' },
        ]}
        rows={paginatedUsers}
        loading={loading}
        rowKey={(u) => u.id}
        onRowClick={(u) => openEditDialog(u)}
        emptyIcon={<Users size={20} className="text-slate-500" />}
        emptyTitle="No users yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Add a user to get started.'}
        testIdPrefix="user"
        actionRender={(u) => (
          <span className="flex items-center justify-end gap-1">
            {u.locked && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleUnlock(u); }}
                className="text-amber-600 hover:text-amber-600 hover:bg-amber-500/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
                data-testid={`unlock-user-${u.id}`}
                title="Unlock account"
              >
                <LockKeyOpen size={14} />
              </Button>
            )}
            {u.id !== currentUser.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleToggleStatus(u); }}
                className={u.status === 'active'
                  ? 'text-rose-600 hover:text-rose-600 hover:bg-rose-500/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0'
                  : 'text-emerald-600 hover:text-emerald-600 hover:bg-emerald-500/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0'}
                data-testid={`toggle-status-${u.id}`}
                title={u.status === 'active' ? 'Deactivate user' : 'Activate user'}
              >
                {u.status === 'active' ? <Prohibit size={14} /> : <CheckCircle size={14} />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); openEditDialog(u); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-user-${u.id}`}
              aria-label="View user"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      {/* Pagination */}
      <DataPagination
        page={safePage}
        pageSize={pageSize}
        total={totalFiltered}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
        testId="users-pagination"
      />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editingUser ? 'Edit User' : (isSuperAdmin ? 'Create Entity Admin' : 'Create User')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingUser ? 'Edit user details' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name <span className="text-[#EF4444]">*</span></Label>
                <Input
                  value={formData.fullname}
                  onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                  placeholder="John Doe"
                  className="border-[#EDE6D8] rounded-lg"
                  required
                  data-testid="user-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{editingUser ? 'New Password (leave blank to keep)' : 'Password'} {!editingUser && <span className="text-[#EF4444]">*</span>}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="border-[#EDE6D8] rounded-lg"
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mobile <span className="text-[#EF4444]">*</span></Label>
                <PhoneInput
                  value={formData.mobile}
                  onChange={(v) => setFormData({ ...formData, mobile: v })}
                  testIdPrefix="user-phone"
                  required
                />
                <p className="text-[11px] text-[#78716C]">Required — also serves as the user's login ID and the maker ID for middleware operations.</p>
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-[#EF4444]">*</span></Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  className="border-[#EDE6D8] rounded-lg"
                  required
                  data-testid="user-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role <span className="text-[#EF4444]">*</span></Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value, entity_id: value === 'super_admin' ? '' : formData.entity_id })}
                >
                  <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" />
            </div>

            {/* Entity selector — only shown for non-super-admin users (super admins don't belong to any entity) */}
            {isSuperAdmin && formData.role !== 'super_admin' && (
              <div className="space-y-2">
                <Label>Assign to Entity <span className="text-[#EF4444]">*</span></Label>
                <Select
                  value={formData.entity_id}
                  onValueChange={(value) => setFormData({ ...formData, entity_id: value })}
                >
                  <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="user-entity-select">
                    <SelectValue placeholder="Select entity" />
                  </SelectTrigger>
                  <SelectContent>
                    {entities.map((entity) => (
                      <SelectItem key={entity.id} value={entity.id}>{entity.entity_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Campus pin — only shown for Campus Correspondent */}
            {formData.role === 'campus_correspondent' && (
              <div className="space-y-2">
                <Label>Assign Campus <span className="text-[#EF4444]">*</span></Label>
                <Select
                  value={formData.campus_id}
                  onValueChange={(value) => setFormData({ ...formData, campus_id: value })}
                  disabled={!formData.entity_id || loadingCampuses}
                >
                  <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="user-campus-select">
                    <SelectValue placeholder={!formData.entity_id ? 'Select an entity first' : (loadingCampuses ? 'Loading…' : 'Select campus')} />
                  </SelectTrigger>
                  <SelectContent>
                    {campusOptions.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.campus_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#78716C]">This correspondent will only see and bill on the chosen campus.</p>
              </div>
            )}

            <DialogFooter className="pt-4 border-t border-[#EDE6D8]">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}
                className="border-[#EDE6D8] rounded-lg">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                data-testid="save-user-btn">
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
